import React from 'react';
import { useTranslation } from 'react-i18next';
import type { MilestoneMessage } from '../types';

interface ContractReferenceNoteProps {
  contractId: string;
  message: MilestoneMessage;
  locked: boolean;
  onDismiss?: () => void;
}

/** Reference only: a chat message must never silently become agreement content. */
export default function ContractReferenceNote({ contractId, message, locked, onDismiss }: ContractReferenceNoteProps) {
  const { t, i18n } = useTranslation();
  const timestamp = new Date(message.createdAt);
  const dateLabel = Number.isNaN(timestamp.getTime()) ? '' : timestamp.toLocaleString(
    i18n.language.startsWith('en') ? 'en-GB' : 'sv-SE',
    { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
  );

  return (
    <aside className="alert alert-info" data-testid="contract-reference-note" data-contract-id={contractId}>
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div>
          <h6 className="mb-1">{t('contractReference.title')}</h6>
          <div className="small">{message.userName}{dateLabel && ` · ${dateLabel}`}</div>
        </div>
        {onDismiss && <button type="button" className="btn-close" onClick={onDismiss} aria-label={t('contractReference.dismiss')} />}
      </div>
      <blockquote className="bg-white border rounded p-3 my-3" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: '16rem', overflowY: 'auto' }}>
        {message.message}
      </blockquote>
      <p className="small mb-0">{t('contractReference.referenceOnly')}</p>
      {locked && <p className="small fw-semibold mt-2 mb-0" data-testid="contract-reference-locked">{t('contractReference.locked')}</p>}
    </aside>
  );
}
